import React from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, ShoppingCart, Store, Users,
  ArrowUpRight, ArrowDownRight, ShieldCheck, 
  Download, Plus, AlertCircle, ChevronRight,
  TrendingUp, Activity, Bell, Package, Star,
  Calendar, Eye, CheckCircle, XCircle, Clock
} from 'lucide-react';
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAdminStats } from '../../redux/adminStatsSlice';
import { fetchSellers } from '../../redux/adminUsersSlice';

// --- DEMO DATA ---
const revenueData = [
  { name: "Mon", revenue: 4200, orders: 15 },
  { name: "Tue", revenue: 3800, orders: 12 },
  { name: "Wed", revenue: 5600, orders: 20 },
  { name: "Thu", revenue: 4900, orders: 18 },
  { name: "Fri", revenue: 6800, orders: 25 },
  { name: "Sat", revenue: 7500, orders: 28 },
  { name: "Sun", revenue: 8200, orders: 32 },
];

const orderStatusData = [
  { name: 'Delivered', value: 65, color: '#10b981' },
  { name: 'Processing', value: 20, color: '#3b82f6' },
  { name: 'Pending', value: 10, color: '#f59e0b' },
  { name: 'Cancelled', value: 5, color: '#ef4444' },
];

const recentOrders = [
  { id: "ORD-9201", customer: "Rahul Sharma", product: "Monstera Deliciosa", amount: "₹1,250", status: "Processing", date: "Just now" },
  { id: "ORD-9200", customer: "Priya Patel", product: "Snake Plant & Pot", amount: "₹890", status: "Delivered", date: "15m ago" },
  { id: "ORD-9199", customer: "Amit Kumar", product: "Garden Tools Set", amount: "₹2,400", status: "Delivered", date: "1h ago" },
  { id: "ORD-9198", customer: "Sneha Gupta", product: "Fiddle Leaf Fig", amount: "₹3,450", status: "Pending", date: "3h ago" },
  { id: "ORD-9197", customer: "Vikram Singh", product: "Rose Seeds Bundle", amount: "₹340", status: "Cancelled", date: "5h ago" },
];

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const { stats, loading: statsLoading, error: statsError } = useSelector((state) => state.adminStats);
  const { sellers } = useSelector((state) => state.adminUsers);

  React.useEffect(() => {
    dispatch(fetchAdminStats());
    dispatch(fetchSellers());
  }, [dispatch]);

  const pendingSellersList = sellers ? sellers.filter(s => s.sellerStatus === 'pending') : [];

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'Delivered': return 'success';
      case 'Processing': return 'info';
      case 'Pending': return 'warning';
      case 'Cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  if (statsLoading) return <div className="p-4">Loading dashboard...</div>;
  if (statsError) return <div className="p-4 text-danger">{statsError}</div>;

  const { totalUsers, totalSellers, pendingSellers: pSellersCount, totalOrders, totalRevenue, recentActivities } = stats || {};


  return (
    <div className="admin-dashboard-modern">
      {/* Header Section */}
      <div className="dashboard-header-modern">
        <div>
          <h1 className="dashboard-title">Overview</h1>
          <p className="dashboard-subtitle">Monitor and manage your entire nursery marketplace.</p>
        </div>
        <div className="dashboard-actions">
          <button className="btn-outline">
            <Download size={16} /> Export Report
          </button>
          <Link to="/admin/products/add" className="btn-primary">
            <Plus size={16} /> Add Product
          </Link>
        </div>
      </div>

      {/* Critical Banner for Action Items */}
      {pendingSellersList.length > 0 && (
        <div className="alert-banner">
          <div className="alert-icon">
            <AlertCircle size={20} />
          </div>
          <div className="alert-content">
            <h6>Pending Seller Approvals ({pSellersCount})</h6>
            <p>New vendors are waiting to join your platform.</p>
          </div>
          <Link to="/admin/sellers/pending" className="alert-btn">
            Review Applications <ChevronRight size={16} />
          </Link>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid-modern">
        <div className="stat-card-modern">
          <div className="stat-card-header">
            <span className="stat-card-title">Total Revenue</span>
            <div className="stat-card-icon blue">
              <DollarSign size={20} />
            </div>
          </div>
          <div className="stat-card-value">₹{totalRevenue?.toLocaleString()}</div>
          <div className="stat-card-trend">
            <ArrowUpRight size={14} className="trend-up-icon" />
            <span className="trend-up">12.5%</span>
            <span className="trend-label">vs last month</span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-header">
            <span className="stat-card-title">Total Orders</span>
            <div className="stat-card-icon green">
              <ShoppingCart size={20} />
            </div>
          </div>
          <div className="stat-card-value">{totalOrders?.toLocaleString()}</div>
          <div className="stat-card-trend">
            <ArrowUpRight size={14} className="trend-up-icon" />
            <span className="trend-up">8.2%</span>
            <span className="trend-label">vs last month</span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-header">
            <span className="stat-card-title">Total Customers</span>
            <div className="stat-card-icon purple">
              <Users size={20} />
            </div>
          </div>
          <div className="stat-card-value">{totalUsers?.toLocaleString()}</div>
          <div className="stat-card-trend">
            <ArrowUpRight size={14} className="trend-up-icon" />
            <span className="trend-up">2.1%</span>
            <span className="trend-label">vs last week</span>
          </div>
        </div>

        <div className="stat-card-modern">
          <div className="stat-card-header">
            <span className="stat-card-title">Active Sellers</span>
            <div className="stat-card-icon orange">
              <Store size={20} />
            </div>
          </div>
          <div className="stat-card-value">{totalSellers?.toLocaleString()}</div>
          <div className="stat-card-trend">
            <ArrowDownRight size={14} className="trend-down-icon" />
            <span className="trend-down">1.5%</span>
            <span className="trend-label">vs last week</span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row-modern">
        {/* Revenue Area Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <div>
              <h5 className="chart-title">Revenue Overview</h5>
              <p className="chart-subtitle">Weekly revenue trends</p>
            </div>
            <select className="period-select">
              <option>Last 7 Days</option>
              <option>Last 30 Days</option>
              <option>This Year</option>
            </select>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(value) => `₹${value/1000}k`} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
                  formatter={(value) => [`₹${value}`, 'Revenue']}
                />
                <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Order Status Distribution */}
        <div className="chart-card">
          <div className="chart-header">
            <h5 className="chart-title">Order Status</h5>
            <p className="chart-subtitle">Distribution by status</p>
          </div>
          <div className="pie-chart-container">
            <ResponsiveContainer width="100%" height="200px">
              <PieChart>
                <Pie
                  data={orderStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {orderStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="legend-list">
            {orderStatusData.map((item, idx) => (
              <div key={item.name} className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: item.color }}></span>
                <span className="legend-label">{item.name}</span>
                <span className="legend-value">{item.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tables Row */}
      <div className="tables-row-modern">
        {/* Recent Orders Table */}
        <div className="table-card">
          <div className="table-header">
            <div>
              <h5 className="table-title">Recent Orders</h5>
              <p className="table-subtitle">Latest transactions from customers</p>
            </div>
            <Link to="/admin/orders" className="view-link">
              View All <ChevronRight size={14} />
            </Link>
          </div>
          <div className="table-responsive">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Order Ref</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentActivities && recentActivities.map((order, idx) => (
                  <tr key={order._id}>
                    <td className="order-id">#{order._id.substring(order._id.length - 6).toUpperCase()}</td>
                    <td>
                      <div className="customer-cell">
                        <div className="customer-avatar">
                          <span>{order.buyer?.username ? order.buyer.username.charAt(0).toUpperCase() : 'U'}</span>
                        </div>
                        <span>{order.buyer?.username || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="product-cell">{order.orderItems?.length || 0} Items</td>
                    <td className="amount-cell">₹{order.totalAmount}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {(!recentActivities || recentActivities.length === 0) && (
                  <tr>
                    <td colSpan="5" className="text-center py-4">No recent orders found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="right-sidebar">
          {/* Seller Insights */}
          <div className="info-card">
            <div className="info-card-header">
              <Store size={18} />
              <h6>Seller Insights</h6>
              <span className="badge-new">{pendingSellersList.length} New</span>
            </div>
            <div className="info-card-body">
              <div className="seller-list">
                {pendingSellersList.map((seller) => (
                  <div key={seller._id} className="seller-item">
                    <div className="seller-info">
                      <h6>{seller.storeName || seller.username}</h6>
                      <p>{seller.email}</p>
                    </div>
                    <Link to={`/admin/sellers/details/${seller._id}`} className="btn-approve">Review</Link>
                  </div>
                ))}
                {pendingSellersList.length === 0 && <p className="text-muted text-center py-2" style={{fontSize: '13px'}}>No pending approvals</p>}
              </div>
              <Link to="/admin/sellers" className="view-all-link">
                Manage All Sellers <ChevronRight size={14} />
              </Link>
            </div>
          </div>

          {/* Live Activity */}
          <div className="info-card">
            <div className="info-card-header">
              <Bell size={18} />
              <h6>Live Activity</h6>
            </div>
            <div className="info-card-body">
              <div className="activity-list">
                {recentActivities && recentActivities.slice(0,3).map((act, i) => (
                  <div key={i} className="activity-item">
                    <div className="activity-icon success">
                      <DollarSign size={14} />
                    </div>
                    <div>
                      <p>Payment of ₹{act.totalAmount} received</p>
                      <span>Order #{act._id.substring(act._id.length - 6).toUpperCase()} from {act.user?.username || 'Unknown'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default AdminDashboard;